import { Grid, Modal } from '@mantine/core';
import { useEffect, useState } from 'react';

import type { Log } from '../pages/Logs';

interface ILogModal {
  opened: boolean;
  close: () => void;
  log: Log | null;
}

const UserAndDate = ({ log }: { log: Log }) => {
  return log.user ? (
    <p>
      {log.user}, at {new Date(log.timestamp).toLocaleString()}
    </p>
  ) : (
    <p>{new Date(log.timestamp).toLocaleString()}</p>
  );
};

const SearchMassLog = ({ log }: { log: Log }) => {
  const message = JSON.parse(log.message);
  const filters = message.scrub.map((s: any) => s.text);
  filters.push(`${message.states.length} States Searched`);
  const totalBad = (Object.values(message.badCount) as number[]).reduce(
    (a, b) => a + b,
    0
  );
  return (
    <div className="flex flex-col gap-y-5">
      <div className="flex flex-row justify-between">
        <UserAndDate log={log} />
        <p>Searched file: {message.filename}</p>
      </div>
      <div>
        <p className="font-semibold text-lg">Filters</p>
        <div className="flex flex-row gap-x-1">
          <p>{filters.join(' - ')}</p>
        </div>
      </div>
      <div>
        <p className="font-semibold text-lg">File Stats</p>
        <div className="flex flex-row gap-x-4">
          <div> Total Contacts: {message.totalCount} </div>
          <div> Total Bad: {totalBad} </div>
          <div> Total Clean: {message.cleanCount} </div>
        </div>
      </div>
      <div>
        <p className="font-semibold text-lg">Phones searched</p>
        <Grid className="mt-0">
          {log.sublogs?.map((s) => (
            <div className="border-black px-2">{s.information}</div>
          ))}
        </Grid>
      </div>
    </div>
  );
};

const SearchSingleLog = ({ log }: { log: Log }) => {
  return (
    <div className="flex flex-col">
      <div className="flex flex-row justify-between">
        <UserAndDate log={log} />
        <p>Searched single contact</p>
      </div>
      <p>Searched for: </p>
      <Grid className="pt-5">
        {log.sublogs?.map((s) => (
          <div className="border-black px-2">{s.information}</div>
        ))}
      </Grid>
    </div>
  );
};

const AddMassLog = ({ log }: { log: Log }) => {
  return (
    <div className="flex flex-col">
      <div className="flex flex-row justify-between">
        <UserAndDate log={log} />
        <p>Added bulk contacts</p>
      </div>
      <p>Phones added: </p>
      <Grid className="pt-5">
        {log.sublogs?.map((s) => (
          <div className="border-black px-2">{s.information}</div>
        ))}
      </Grid>
    </div>
  );
};

const AddSingleLog = ({ log }: { log: Log }) => {
  const newContact = JSON.parse(log.sublogs[0]?.information || '{}');
  return (
    <div className="flex flex-col">
      <div className="flex flex-row justify-between">
        <UserAndDate log={log} />
        <p>Added single contact</p>
      </div>
      <div className="flex flex-col gap-y-1 pt-4">
        <p>Contact added: </p>
        <div className="">
          {newContact.phone && (
            <div className="">Phone: {newContact.phone}</div>
          )}
          {newContact.name && <div className="">Name: {newContact.name}</div>}
          {newContact.email && (
            <div className="">Email: {newContact.email}</div>
          )}
        </div>
      </div>
    </div>
  );
};

const OptOutLog = ({ log }: { log: Log }) => {
  const newContact = JSON.parse(log.sublogs[0]?.information || '{}');
  return (
    <div className="flex flex-col">
      <div className="flex flex-row justify-between">
        <UserAndDate log={log} />
        <p>New Opt Out contact</p>
      </div>
      <div className="flex flex-col gap-y-1 pt-4">
        <p>Opted out contact: </p>
        <div className="">
          {newContact.phone && (
            <div className="">Phone: {newContact.phone}</div>
          )}
          {newContact.name && <div className="">Name: {newContact.name}</div>}
          {newContact.email && (
            <div className="">Email: {newContact.email}</div>
          )}
        </div>
      </div>
    </div>
  );
};

const LogDetailsModal = ({ opened, close, log }: ILogModal) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState(<></>);

  const reset = () => {
    setTitle('');
    setContent(<></>);
  };

  const generateLogDetails = () => {
    if (!log) return reset();
    switch (log.type) {
      case 'search_mass':
        setTitle('Bulk Search');
        setContent(<SearchMassLog log={log} />);
        break;

      case 'search_single':
        setTitle('Single Search');
        setContent(<SearchSingleLog log={log} />);
        break;

      case 'add_mass':
        setTitle('Add Bulk Phones');
        setContent(<AddMassLog log={log} />);
        break;

      case 'add_single':
        setTitle('Add Single Phone');
        setContent(<AddSingleLog log={log} />);
        break;

      case 'add_opt_out':
        setTitle('New Opt Out');
        setContent(<OptOutLog log={log} />);
        break;

      default:
        setTitle('');
        setContent(<></>);
    }
    return null;
  };
  useEffect(() => {
    generateLogDetails();
  }, [log]);
  return (
    <Modal
      opened={opened}
      title={title}
      classNames={{
        title: 'font-bold text-xl',
      }}
      size="xl"
      onClose={() => {
        close();
        setTitle('');
      }}
      centered
    >
      {content}
    </Modal>
  );
};

export default LogDetailsModal;
